if (typeof ProductQuantity !== 'function') {

	class ProductQuantity extends HTMLElement {

		constructor() {
			super();

			// --- Find Elements ---
			this.qtyInput = this.querySelector('.qty-selector');
			this.qtyMinusBtn = this.querySelector('.qty-minus');
			this.qtyPlusBtn = this.querySelector('.qty-plus');

			// --- Essential Check ---
			if (!this.qtyInput || !this.qtyMinusBtn || !this.qtyPlusBtn) {
				console.error('ProductQuantity init error: Missing required child elements (.qty-selector, .qty-minus, .qty-plus) within:', this);
				return;
			}
            // console.log('ProductQuantity constructor running for:', this.qtyInput.id || 'unknown input');


			// --- Get Quantity Rules ---
            const ruleSourceElement = this.closest('[data-step-rule]') || this;
            const requiresStep6 = ruleSourceElement.dataset.stepRule === '6';

            this.step = requiresStep6 ? 6 : 1;
            this.functionalMin = requiresStep6 ? 6 : 1;
			this.absMin = this.qtyInput.getAttribute('min') ? parseInt(this.qtyInput.getAttribute('min')) : 1; // Should be 1
            this.effectiveMin = Math.max(this.absMin, this.functionalMin);
			this.absMax = this.qtyInput.getAttribute('max') ? parseInt(this.qtyInput.getAttribute('max')) : Infinity;


			// --- Bind Methods and Add Listeners ---
			this.handleMinusClick = this.handleMinusClick.bind(this);
			this.handlePlusClick = this.handlePlusClick.bind(this);
			this.handleInputChange = this.handleInputChange.bind(this);
			this.updateButtonStates = this.updateButtonStates.bind(this);

			this.qtyMinusBtn.addEventListener('click', this.handleMinusClick);
			this.qtyPlusBtn.addEventListener('click', this.handlePlusClick);
            // Listen for 'change' to handle manual input corrections and update buttons
			this.qtyInput.addEventListener('change', this.handleInputChange);


			// --- Initial State ---
            // Run initial correction/validation in case the starting value is wrong
            this.handleInputChange();
			this.updateButtonStates();
		}

		// Method to update button disabled states based on current value and rules
		updateButtonStates() {
			if (!this.qtyInput) return;

			const currentQty = parseInt(this.qtyInput.value);
            if (isNaN(currentQty)) return;

            this.qtyMinusBtn.classList.toggle('disabled', currentQty <= this.effectiveMin);
			this.qtyPlusBtn.classList.toggle('disabled', currentQty + this.step > this.absMax);
		}

		// Handle Minus Button Click
		handleMinusClick(e) {
			e.preventDefault();
			if (this.qtyMinusBtn.classList.contains('disabled')) return;

			const currentQty = parseInt(this.qtyInput.value);
			let newQty = currentQty - this.step;

			// Ensure quantity doesn't go below the *effective* minimum (1 or 6)
			newQty = Math.max(this.effectiveMin, newQty);

			if (newQty !== currentQty) {
				this.qtyInput.value = newQty;
				// --- CRUCIAL: Dispatch change event ---
				this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
                // updateButtonStates will be called by the handleInputChange listener
			} else {
                this.updateButtonStates(); // Update buttons if value didn't change (e.g., already at min)
            }
		}

		// Handle Plus Button Click
		handlePlusClick(e) {
			e.preventDefault();
			if (this.qtyPlusBtn.classList.contains('disabled')) return;

			const currentQty = parseInt(this.qtyInput.value);
            let newQty = 0;

            // Jump to minimum if current value is somehow below it
            if(currentQty < this.effectiveMin) {
                newQty = this.effectiveMin;
            } else {
			    newQty = currentQty + this.step;
            }

			newQty = Math.min(this.absMax, newQty); // Ensure not over max

            if (newQty !== currentQty) {
			    this.qtyInput.value = newQty;
			    // --- CRUCIAL: Dispatch change event ---
			    this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
                 // updateButtonStates will be called by the handleInputChange listener
            } else {
                this.updateButtonStates(); // Update buttons if value didn't change (e.g., already at max)
            }
		}

		// Handle Manual Input Change (Validate and Correct Value)
		handleInputChange() {
            let currentRawValue = this.qtyInput.value;
            let currentQty = parseInt(currentRawValue);
            let correctedQty = currentQty; // Assume correct initially

            // console.log(`Input change detected for ${this.qtyInput.id}, raw value: ${currentRawValue}, parsed: ${currentQty}`);

            // 1. Basic Validation: Not a Number or below absolute minimum (1)
            if (isNaN(currentQty) || currentQty < this.absMin) {
                // If invalid, reset to the required effective minimum (1 or 6)
                correctedQty = this.effectiveMin;
                // console.log(`Invalid input (${currentRawValue}), resetting to effective minimum: ${correctedQty}`);
            } else {
                // 2. Enforce Functional Minimum (e.g., 6 if needed)
                // If it's a valid number but below the required functional minimum (and not 0, though absMin=1 prevents 0)
                if (currentQty < this.effectiveMin) {
                    correctedQty = this.effectiveMin;
                    // console.log(`Quantity ${currentQty} is below functional minimum ${this.effectiveMin}, correcting to ${correctedQty}`);
                }
                // 3. Enforce Step Rule (only if step > 1 and quantity > 0)
                else if (this.step > 1 && currentQty > 0 && (currentQty % this.step !== 0)) {
                    // Round UP to the nearest multiple of the step
                    correctedQty = Math.ceil(currentQty / this.step) * this.step;
                    // console.warn(`Quantity ${currentQty} is not a multiple of ${this.step}, rounding up to ${correctedQty}`);
                }
            }

            // 4. Ensure it doesn't exceed max after potential adjustments
            correctedQty = Math.min(this.absMax, correctedQty);

            // 5. Update the input field *only if* correction occurred
            // Check against parsed initial value to handle potential whitespace etc.
            if (correctedQty !== currentQty) {
                // console.log(`Correcting input value from ${currentRawValue} to ${correctedQty}`);
                this.qtyInput.value = correctedQty;
                // NOTE: No need to dispatch 'change' again, this *is* the change handler.
                // The browser triggers 'change' when the user finishes editing.
            } else {
                 // console.log(`Input value ${currentQty} is valid or unchanged.`);
            }

            // Always update button states after any input change/validation
            this.updateButtonStates();
		}
	}

	// Define the custom element if it hasn't been defined yet
	if (typeof customElements.get('product-quantity') === 'undefined') {
		customElements.define('product-quantity', ProductQuantity);
	}
}